import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Pencil, Upload, Users, Network, History, ArrowRightLeft, Trash2, Loader2, CheckCircle2, AlertCircle, Circle, ShieldCheck, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAccessGroups, GroupSelect } from "@/components/bi/AccessGrants";
import { ManagedCopyDialog } from "@/components/bi/ManagedCopyDialog";
import { useAuth } from "@/context/AuthContext";
import { isCrossTenant } from "@/lib/roles";

// Item 9: live connection / storage status dot (lazy, cached server-side for 60s)
export function DatasetStatusDot({ ds, refreshKey = 0 }) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    let active = true;
    api.get(`/datasets/${ds.id}/status`).then(({ data }) => { if (active) setSt(data); }).catch(() => { if (active) setSt({ status: "unknown", message: "Status unavailable" }); });
    return () => { active = false; };
  }, [ds.id, refreshKey]);
  const Icon = !st ? Loader2 : st.status === "ok" ? CheckCircle2 : st.status === "error" ? AlertCircle : Circle;
  const color = !st ? "text-[color:var(--ink-3)]" : st.status === "ok" ? "text-[color:var(--success)]" : st.status === "error" ? "text-[color:var(--danger)]" : "text-[color:var(--ink-3)]";
  const title = st ? `${st.message}${st.latency_ms != null ? ` · ${st.latency_ms} ms` : ""}${st.last_refresh ? `\nLast refresh: ${new Date(st.last_refresh.ts).toLocaleString()} (${st.last_refresh.status})` : ""}` : "Checking…";
  return (
    <span data-testid="dataset-status" data-status={st?.status || "loading"} title={title} className={cn("inline-flex items-center gap-1 text-[10px]", color)}>
      <Icon className={cn("w-3 h-3", !st && "animate-spin")} />{st ? (st.status === "ok" ? "connected" : st.status === "error" ? "error" : "unknown") : "checking"}
    </span>
  );
}

const fmtTs = (s) => (s ? new Date(s).toLocaleString() : "—");

export function DatasetCardActions({ ds, onChanged, onDeleted }) {
  const { user } = useAuth();
  const staff = isCrossTenant(user?.role);
  const [dlg, setDlg] = useState(null); // rename | replace | share | deps | history | transfer | delete | provision
  const managed = ds.managed_source || (ds.managed && !staff ? ds.managed : null);
  const canManage = (ds.my_role === "owner" || ds.my_role === "admin" || ds.my_role === undefined) && (!managed || staff);
  const canEdit = canManage || ds.my_role === "editor";
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button data-testid="dataset-more-btn" size="sm" variant="ghost" className="h-8 w-8 p-0 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]" title="More actions"><MoreHorizontal className="w-4 h-4" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {managed && (
            <DropdownMenuItem data-testid="ds-managed-note" disabled className="text-[11px] text-[color:var(--ink-3)] whitespace-normal">
              <ShieldCheck className="w-4 h-4 mr-2 shrink-0 text-[color:var(--brand-mint)]" />Managed by Eazy — connection details stay hidden
            </DropdownMenuItem>
          )}
          {canEdit && <DropdownMenuItem data-testid="ds-action-rename" onClick={() => setDlg("rename")}><Pencil className="w-4 h-4 mr-2" />Rename</DropdownMenuItem>}
          {canEdit && ds.mode !== "live" && <DropdownMenuItem data-testid="ds-action-replace" onClick={() => setDlg("replace")}><Upload className="w-4 h-4 mr-2" />Replace data…</DropdownMenuItem>}
          <DropdownMenuItem data-testid="ds-action-deps" onClick={() => setDlg("deps")}><Network className="w-4 h-4 mr-2" />Dependencies</DropdownMenuItem>
          <DropdownMenuItem data-testid="ds-action-history" onClick={() => setDlg("history")}><History className="w-4 h-4 mr-2" />Refresh history</DropdownMenuItem>
          {staff && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="ds-action-provision" onClick={() => setDlg("provision")}><Building2 className="w-4 h-4 mr-2" />Provision into a tenant…</DropdownMenuItem>
            </>
          )}
          {canManage && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="ds-action-share" onClick={() => setDlg("share")}><Users className="w-4 h-4 mr-2" />Sharing & permissions</DropdownMenuItem>
              <DropdownMenuItem data-testid="ds-action-transfer" onClick={() => setDlg("transfer")}><ArrowRightLeft className="w-4 h-4 mr-2" />Transfer ownership</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="ds-action-delete" onClick={() => setDlg("delete")} className="text-[color:var(--danger)]"><Trash2 className="w-4 h-4 mr-2" />Delete…</DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {dlg === "rename" && <RenameDialog ds={ds} onClose={() => setDlg(null)} onChanged={onChanged} />}
      {dlg === "replace" && <ReplaceDialog ds={ds} onClose={() => setDlg(null)} onChanged={onChanged} />}
      {dlg === "deps" && <DepsDialog ds={ds} onClose={() => setDlg(null)} />}
      {dlg === "history" && <HistoryDialog ds={ds} onClose={() => setDlg(null)} />}
      {dlg === "share" && <ShareDialog ds={ds} onClose={() => setDlg(null)} onChanged={onChanged} />}
      {dlg === "transfer" && <TransferDialog ds={ds} onClose={() => setDlg(null)} onChanged={onChanged} />}
      {dlg === "delete" && <DeleteDialog ds={ds} onClose={() => setDlg(null)} onDeleted={onDeleted} />}
      <ManagedCopyDialog open={dlg === "provision"} onOpenChange={(o) => { if (!o) setDlg(null); }} kind="dataset"
        item={ds} onDone={() => { setDlg(null); onChanged?.(); }} />
    </>
  );
}

function Frame({ title, desc, children, footer, onClose, testid, wide }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={testid} className={cn(wide ? "max-w-2xl" : "max-w-md", "max-h-[85vh] overflow-y-auto")}>
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle>{desc && <DialogDescription>{desc}</DialogDescription>}</DialogHeader>
        {children}
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

function RenameDialog({ ds, onClose, onChanged }) {
  const [name, setName] = useState(ds.name || "");
  const save = async () => {
    if (!name.trim()) return toast.error("Name cannot be empty");
    try { await api.patch(`/datasets/${ds.id}`, { name: name.trim() }); toast.success("Renamed"); onChanged(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };
  return (
    <Frame testid="ds-rename-dialog" title="Rename dataset" onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button data-testid="ds-rename-save" className="bg-[color:var(--brand-ocean)] text-white" onClick={save}>Save</Button></>}>
      <Input data-testid="ds-rename-input" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} className="bg-[color:var(--surface)]" />
    </Frame>
  );
}

function DiffList({ diff }) {
  if (!diff) return null;
  return (
    <div data-testid="ds-replace-diff" className="space-y-1.5 text-xs">
      <div className="flex gap-3 text-[color:var(--ink-2)]"><span>Rows: <b>{diff.rows_before?.toLocaleString?.() ?? "—"}</b> → <b>{diff.rows_after?.toLocaleString?.()}</b></span></div>
      {diff.missing_columns?.length > 0 && <p className="text-[color:var(--danger)]"><b>Missing columns:</b> {diff.missing_columns.join(", ")} — widgets using them will show no data.</p>}
      {diff.added_columns?.length > 0 && <p className="text-[color:var(--success)]"><b>New columns:</b> {diff.added_columns.join(", ")}</p>}
      {diff.type_changes?.length > 0 && <p className="text-[#B45309]"><b>Type changes:</b> {diff.type_changes.map((t) => `${t.field} (${t.from} → ${t.to})`).join(", ")}</p>}
      {diff.affected_dashboards?.length > 0 && <p className="text-[color:var(--ink-3)]">Affected dashboards: {diff.affected_dashboards.map((d) => d.name).join(", ")}</p>}
      {!diff.missing_columns?.length && !diff.type_changes?.length && <p className="text-[color:var(--success)] flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />Schema compatible — all dashboards keep working.</p>}
    </div>
  );
}

function ReplaceDialog({ ds, onClose, onChanged }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [diff, setDiff] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = async (f) => {
    setFile(f); setDiff(null); setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post(`/datasets/${ds.id}/replace?dry_run=true`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setDiff(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not read file"); setFile(null); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/datasets/${ds.id}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Data replaced — ${data.rows_after?.toLocaleString()} rows`); onChanged(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Replace failed"); }
    finally { setBusy(false); }
  };
  return (
    <Frame testid="ds-replace-dialog" title="Replace data" desc="Upload a new file. The dataset keeps its id, name and model, so every dashboard stays bound." onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button data-testid="ds-replace-apply" disabled={!file || busy} className="bg-[color:var(--brand-ocean)] text-white" onClick={apply}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Replace"}</Button></>}>
      <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.json" className="hidden" data-testid="ds-replace-file" onChange={(e) => e.target.files?.[0] && check(e.target.files[0])} />
      <Button variant="outline" onClick={() => fileRef.current?.click()} className="bg-[color:var(--surface)]"><Upload className="w-4 h-4 mr-1" />{file ? file.name : "Choose file…"}</Button>
      {busy && !diff && <p className="text-xs text-[color:var(--ink-3)] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Checking schema…</p>}
      <DiffList diff={diff} />
    </Frame>
  );
}

function DepsDialog({ ds, onClose }) {
  const [deps, setDeps] = useState(null);
  useEffect(() => { api.get(`/datasets/${ds.id}/dependencies`).then(({ data }) => setDeps(data)).catch(() => setDeps({ total: 0 })); }, [ds.id]);
  const Section = ({ label, items, href }) => !items?.length ? null : (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)]">{label} · {items.length}</p>
      <ul className="space-y-0.5 mt-1">{items.map((it) => (
        <li key={it.id} className="text-xs text-[color:var(--ink-2)] flex items-center gap-2">
          {href ? <a className="hover:underline text-[color:var(--brand-ocean)]" href={href(it)}>{it.name || it.id}</a> : <span>{it.name || it.id}</span>}
          {it.widget_count != null && <span className="text-[10px] text-[color:var(--ink-3)]">{it.widget_count} widget{it.widget_count === 1 ? "" : "s"}</span>}
          {it.status && <Badge variant="outline" className="text-[9px] py-0">{it.status}</Badge>}
        </li>))}</ul>
    </div>
  );
  return (
    <Frame testid="ds-deps-dialog" title={`Dependencies — ${ds.name}`} desc="Everything that reads from this dataset." onClose={onClose}>
      {!deps ? <Loader2 className="w-4 h-4 animate-spin" /> : deps.total === 0 ? <p data-testid="ds-deps-empty" className="text-sm text-[color:var(--ink-3)]">Nothing depends on this dataset yet.</p> : (
        <div className="space-y-3" data-testid="ds-deps-list">
          <Section label="Dashboards" items={deps.dashboards} href={(d) => `/builder/${d.id}`} />
          <Section label="Alerts" items={deps.alerts} />
          <Section label="Reports" items={deps.reports} />
          <Section label="API schedules" items={deps.api_schedules} />
          <Section label="Derived datasets (prep)" items={deps.derived_datasets} />
          <Section label="Join contracts" items={deps.join_contracts} />
        </div>
      )}
    </Frame>
  );
}

function HistoryDialog({ ds, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(`/datasets/${ds.id}/refresh-history`).then(({ data }) => setItems(data.items || [])).catch(() => setItems([])); }, [ds.id]);
  return (
    <Frame testid="ds-history-dialog" title={`Refresh history — ${ds.name}`} onClose={onClose} wide>
      {!items ? <Loader2 className="w-4 h-4 animate-spin" /> : !items.length ? <p className="text-sm text-[color:var(--ink-3)]">No refreshes recorded yet. Use “Refresh now” on the card.</p> : (
        <table className="w-full text-xs" data-testid="ds-history-table">
          <thead><tr className="text-left text-[10px] uppercase tracking-wide text-[color:var(--ink-3)]"><th className="py-1">When</th><th>Kind</th><th>Status</th><th>Rows</th><th>Duration</th><th>By</th><th>Message</th></tr></thead>
          <tbody>{items.map((h) => (
            <tr key={h.id} className="border-t border-[color:var(--glass-border)]">
              <td className="py-1 whitespace-nowrap">{fmtTs(h.ts)}</td><td>{h.kind}</td>
              <td><span className={h.status === "ok" ? "text-[color:var(--success)]" : "text-[color:var(--danger)]"}>{h.status}</span></td>
              <td>{h.rows?.toLocaleString?.() ?? "—"}</td><td>{h.duration_ms} ms</td><td className="truncate max-w-[120px]">{h.by}</td><td className="truncate max-w-[200px]" title={h.message}>{h.message}</td>
            </tr>))}</tbody>
        </table>
      )}
    </Frame>
  );
}

function ShareDialog({ ds, onClose, onChanged }) {
  const [acc, setAcc] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [gid, setGid] = useState("");
  const [gRole, setGRole] = useState("viewer");
  const { all: groups } = useAccessGroups();
  const load = () => api.get(`/datasets/${ds.id}/access`).then(({ data }) => setAcc(data)).catch(() => setAcc({ shared_with: [] }));
  useEffect(() => { load(); }, [ds.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => {
    if (!email.trim()) return toast.error("Enter an email");
    try { const { data } = await api.post(`/datasets/${ds.id}/access`, { email: email.trim(), role }); setAcc(data); setEmail(""); toast.success("Shared"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addGroup = async () => {
    if (!gid) return toast.error("Pick a group");
    try { const { data } = await api.post(`/datasets/${ds.id}/access/group`, { group_id: gid, role: gRole }); setAcc(data); setGid(""); toast.success("Shared with group"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (s) => {
    try { const { data } = await api.delete(s.group_id ? `/datasets/${ds.id}/access/group/${s.group_id}` : `/datasets/${ds.id}/access/${s.user_id}`); setAcc(data); onChanged(); }
    catch { toast.error("Failed"); }
  };
  const setVis = async (v) => { try { const { data } = await api.post(`/datasets/${ds.id}/visibility`, { visibility: v }); setAcc(data); toast.success(v === "tenant" ? "Visible to the whole workspace" : "Private"); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const sharedGroupIds = (acc?.shared_with || []).filter((s) => s.group_id).map((s) => s.group_id);
  return (
    <Frame testid="ds-share-dialog" title={`Sharing — ${ds.name}`} desc="Viewers can build dashboards on this dataset; editors can also change its query, calculated fields and data. Dashboard viewers need access here too — a dashboard grant alone never exposes data." onClose={onClose}>
      {!acc ? <Loader2 className="w-4 h-4 animate-spin" /> : (
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-lg bg-[color:var(--surface-2)] px-3 py-2">
            <span className="text-xs text-[color:var(--ink-2)]">Owner: <b data-testid="ds-owner-email">{acc.owner_email || "—"}</b></span>
            <Select value={acc.visibility || "private"} onValueChange={setVis}>
              <SelectTrigger data-testid="ds-visibility" className="h-7 w-[170px] text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="private">Private (owner + shared)</SelectItem><SelectItem value="tenant">Whole workspace (view)</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Input data-testid="ds-share-email" placeholder="user@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[color:var(--surface)] h-8 text-xs" />
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="ds-share-role" className="h-8 w-[110px] text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="viewer">Viewer</SelectItem><SelectItem value="editor">Editor</SelectItem></SelectContent>
            </Select>
            <Button data-testid="ds-share-add" size="sm" className="h-8 bg-[color:var(--brand-ocean)] text-white" onClick={add}>Share</Button>
          </div>
          <div className="flex gap-2">
            <div className="flex-1 min-w-0"><GroupSelect value={gid} onChange={setGid} groups={groups} exclude={sharedGroupIds} testid="ds-share-group" placeholder="Share with a group…" /></div>
            <Select value={gRole} onValueChange={setGRole}>
              <SelectTrigger data-testid="ds-share-group-role" className="h-8 w-[110px] text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="viewer">Viewer</SelectItem><SelectItem value="editor">Editor</SelectItem></SelectContent>
            </Select>
            <Button data-testid="ds-share-group-add" size="sm" variant="outline" className="h-8" disabled={!gid} onClick={addGroup}>Share</Button>
          </div>
          <div className="space-y-1">
            {(acc.shared_with || []).map((s) => (
              <div key={s.group_id || s.user_id} data-testid="ds-share-row" className="flex items-center justify-between rounded-lg border border-[color:var(--glass-border)] px-3 py-1.5 text-xs">
                <span className="truncate flex items-center gap-1.5">{s.group_id && <Users className="w-3.5 h-3.5 text-[color:var(--brand-ocean)]" />}{s.group_id ? s.group_name || s.group_id : s.email}{s.via === "m0008" && <span className="text-[10px] text-[color:var(--ink-3)]" title="Added automatically by the upgrade to keep existing shared dashboards working">· migrated</span>}</span>
                <span className="flex items-center gap-2"><Badge variant="outline" className="text-[10px]">{s.role}</Badge><button data-testid="ds-share-remove" onClick={() => remove(s)} className="text-[color:var(--danger)]"><Trash2 className="w-3.5 h-3.5" /></button></span>
              </div>))}
            {!(acc.shared_with || []).length && <p className="text-xs text-[color:var(--ink-3)]">Not shared with anyone yet.</p>}
          </div>
        </div>
      )}
    </Frame>
  );
}

function TransferDialog({ ds, onClose, onChanged }) {
  const [email, setEmail] = useState("");
  const [keep, setKeep] = useState("editor");
  const go = async () => {
    if (!email.trim()) return toast.error("Enter the new owner's email");
    try { const { data } = await api.post(`/datasets/${ds.id}/transfer-owner`, { email: email.trim(), keep_previous_owner_as: keep }); toast.success(`Ownership transferred to ${data.owner_email}`); onChanged(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Transfer failed"); }
  };
  return (
    <Frame testid="ds-transfer-dialog" title="Transfer ownership" desc="The new owner controls sharing, editing and deletion. The semantic model moves with the dataset." onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button data-testid="ds-transfer-confirm" className="bg-[color:var(--brand-ocean)] text-white" onClick={go}>Transfer</Button></>}>
      <div className="flex gap-2">
        <Input data-testid="ds-transfer-email" placeholder="new-owner@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[color:var(--surface)] h-8 text-xs" />
        <Select value={keep} onValueChange={setKeep}>
          <SelectTrigger data-testid="ds-transfer-keep" className="h-8 w-[160px] text-xs bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="editor">I keep: Editor</SelectItem><SelectItem value="viewer">I keep: Viewer</SelectItem><SelectItem value="none">I keep: nothing</SelectItem></SelectContent>
        </Select>
      </div>
    </Frame>
  );
}

function DeleteDialog({ ds, onClose, onDeleted }) {
  const [deps, setDeps] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/datasets/${ds.id}/dependencies`).then(({ data }) => setDeps(data)).catch(() => setDeps({ total: 0, dashboards: [] })); }, [ds.id]);
  const del = async (force) => {
    setBusy(true);
    try { await api.delete(`/datasets/${ds.id}${force ? "?force=true" : ""}`); toast.success("Dataset deleted"); onDeleted(); onClose(); }
    catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : d?.message || "Delete failed"); }
    finally { setBusy(false); }
  };
  const blocked = deps && deps.total > 0;
  return (
    <Frame testid="ds-delete-dialog" title={`Delete “${ds.name}”?`} onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button data-testid="ds-delete-confirm" disabled={!deps || busy} variant="destructive" onClick={() => del(!!blocked)}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : blocked ? `Delete anyway (${deps.total} dependent)` : "Delete"}</Button></>}>
      {!deps ? <Loader2 className="w-4 h-4 animate-spin" /> : blocked ? (
        <div data-testid="ds-delete-blocked" className="text-xs space-y-1">
          <p className="text-[color:var(--danger)] flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{deps.total} item{deps.total === 1 ? "" : "s"} depend on this dataset and will break:</p>
          <ul className="list-disc pl-5 text-[color:var(--ink-2)]">
            {deps.dashboards.map((d) => <li key={d.id}>Dashboard “{d.name}” ({d.widget_count} widget{d.widget_count === 1 ? "" : "s"})</li>)}
            {deps.alerts?.map((a) => <li key={a.id}>Alert “{a.name}”</li>)}
            {deps.reports?.map((r) => <li key={r.id}>Report “{r.name}”</li>)}
            {deps.api_schedules?.map((r) => <li key={r.id}>API schedule “{r.name}”</li>)}
            {deps.derived_datasets?.map((r) => <li key={r.id}>Derived dataset “{r.name}”</li>)}
            {deps.join_contracts?.map((r) => <li key={r.id}>Join contract “{r.name}”</li>)}
          </ul>
        </div>
      ) : <p className="text-sm text-[color:var(--ink-3)]">Nothing depends on this dataset. This cannot be undone.</p>}
    </Frame>
  );
}
